// AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    if (storedUser && storedUser !== 'undefined' && storedToken && storedToken !== 'undefined') {
      try {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  // Sprawdzanie ważności tokena przy kliknięciach w aplikacji
  useEffect(() => {
    const checkAuth = () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (!storedToken || !storedUser || storedToken === 'undefined' || storedUser === 'undefined') {
        clearAuth();
      }
    };

    document.addEventListener('click', checkAuth);
    return () => document.removeEventListener('click', checkAuth);
  }, [clearAuth]);

  const login = (userData, token) => {
    setUser(userData);
    setToken(token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', token);
  };

  const logout = () => {
    clearAuth();
  };

  // Mapa uprawnień -> ścieżek, w kolejności priorytetowej
  const PERM_ROUTES = [
    { perm: "docs_list", path: "/dokumenty" },
    { perm: "vehicles_dashboard", path: "/pojazdy" },
    { perm: "vehicles_list", path: "/pojazdy/lista" },
    { perm: "vehicles_services", path: "/serwisy" },
    { perm: "vehicles_repairs", path: "/naprawy" },
    { perm: "vehicles_faults", path: "/zgloszenia" },
    { perm: "vehicles_workshop", path: "/warsztat" },
    { perm: "vehicles_statistics", path: "/statystyki" },
    { perm: "vehicles_settings", path: "/ustawienia-pojazdow" },
    { perm: "docs_create", path: "/dokumenty/nowy" },
    { perm: "docs_stats", path: "/dokumenty/statystyki" },
    { perm: "admin_panel", path: "/admin" },
  ];

  // Sprawdza uprawnienie użytkownika.
  // Administratorzy bez zapisanych uprawnień mają dostęp do wszystkiego.
  // Użytkownicy bez zapisanych uprawnień mają dostęp do wszystkiego oprócz admin_panel.
  const hasPermission = (permKey) => {
    if (!user) return false;
    const perms = user.permissions;
    if (!perms || Object.keys(perms).length === 0) {
      if (user.role === "Administrator") return true;
      return permKey !== "admin_panel";
    }
    return !!perms[permKey];
  };

  // Zwraca pierwszą dozwoloną ścieżkę dla użytkownika
  const getFirstPermittedPath = (userData) => {
    const u = userData || user;
    if (!u) return "/login";
    const perms = u.permissions;
    const check = (key) => {
      if (!perms || Object.keys(perms).length === 0) {
        if (u.role === "Administrator") return true;
        return key !== "admin_panel";
      }
      return !!perms[key];
    };
    for (const route of PERM_ROUTES) {
      if (check(route.perm)) return route.path;
    }
    return "/login";
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, hasPermission, getFirstPermittedPath }}>
      {children}
    </AuthContext.Provider>
  );
};
